import React, { useState } from "react";
import AppChip, { AppStatusChip } from "../../../../components/AppChip";
import AppTable from "../../../../components/AppTable";
import AppTablePane from "../../../../components/AppTablePane";
import AppTableToolbar from "../../../../components/AppTableToolbar";
import AppText from "../../../../components/AppText";
import {
  IconDelete,
  IconExport,
  IconImport,
} from "../../../../icons";
import DocsNiceToKnow from "../../shared/DocsNiceToKnow";
import DocsPreviewPanel from "../../shared/DocsPreviewPanel";
import DocsSectionCard from "../../shared/DocsSectionCard";
import { sampleTableRows } from "../../shared/patternData";

const tableRows = sampleTableRows.map((row) => ({
  ...row,
  defaultText: row.identity || "-",
  titleText: row.area,
  secondaryText: row.email,
  chipText: row.entity || row.count,
}));

const tableColumns = [
  { key: "selected", label: "", controlKind: "selection", sortable: false },
  {
    key: "defaultText",
    label: "Default",
    minWidth: 92,
    maxWidth: 120,
    grow: 0,
  },
  {
    key: "titleText",
    label: "Title",
    minWidth: 180,
    grow: 2,
    render: (value) => (
      <AppText variant="heading-4" tone="primary" truncate>
        {value}
      </AppText>
    ),
  },
  {
    key: "secondaryText",
    label: "Text",
    minWidth: 160,
    grow: 1,
    render: (value) => (
      <AppText variant="body-1" tone="secondary" truncate>
        {value}
      </AppText>
    ),
  },
  {
    key: "chipText",
    label: "Chip",
    minWidth: 86,
    maxWidth: 112,
    grow: 0,
    align: "center",
    render: (value) => (
      <AppChip variant="primary" appearance="outlined" size="sm">
        {value}
      </AppChip>
    ),
  },
  {
    key: "status",
    label: "Status",
    minWidth: 108,
    maxWidth: 132,
    grow: 0,
    render: (value) => (
      <AppStatusChip status={value.status} size="sm">
        {value.label}
      </AppStatusChip>
    ),
  },
  { key: "menu", label: "", controlKind: "menu", sortable: false },
];

const niceToKnowItems = [
  "Zelltypen sind direkt in der Vorschau-Tabelle sichtbar.",
  "Footer, Empty State, Sorting, Selection und Loading kommen aus AppTable.",
  "density=\"compact\" verwendet Body 2 und kompaktere Zeilen für Vorschauen und dichte Übersichten.",
  "Editierbare Zellen werden über editable, renderEdit und onCellEditCommit definiert.",
  "AppTableToolbar optional oberhalb der Tabelle.",
  "Verknüpfung von Search, Buttons und Tabelle jeweils lokal lösen.",
  "Text-Values werden bei zu wenig Platz automatisch mit ... gekürzt.",
];

const columnDefinitionHighlights = [
  { number: "-", label: "Breite", text: "width, minWidth, maxWidth" },
  { number: "-", label: "Grow", text: "0 = kompakt/fix, 1 = normal flexibel, 2 = bekommt mehr Restplatz" },
  { number: "-", label: "Ausrichtung", text: "align left oder center" },
  { number: "-", label: "Sortierung", text: "sortable true/false" },
  { number: "-", label: "Render", text: "optional. Ohne render body-1 / primary / truncate" },
  { number: "-", label: "Bearbeiten", text: "editable + renderEdit; Speichern über onCellEditCommit" },
];

function MainTablePreview() {
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const visibleRows = normalizedSearchQuery
    ? tableRows.filter((row) =>
        [row.defaultText, row.titleText, row.secondaryText, row.chipText, row.status?.label].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(normalizedSearchQuery)
        )
      )
    : tableRows;
  const hasSelection = selectedRowIds.length > 0;
  const toolbarUtilityActions = [
    { label: "Importieren", icon: <IconImport sx={{ fontSize: 18 }} />, iconOnly: true },
    { label: "Exportieren", icon: <IconExport sx={{ fontSize: 18 }} />, iconOnly: true, disabled: !hasSelection },
    {
      label: "Löschen",
      icon: <IconDelete sx={{ fontSize: 18 }} />,
      iconOnly: true,
      disabled: !hasSelection,
      variant: hasSelection ? "error" : "muted",
    },
  ];

  return (
    <div className="ds-docs-table-preview">
      <AppTableToolbar
        search={{
          value: searchQuery,
          placeholder: "Suchen...",
          onChange: (event) => setSearchQuery(event.target.value),
        }}
        utilityActions={toolbarUtilityActions}
      />
      <AppTablePane className="ds-docs-table-preview__pane">
        <AppTable
          columns={tableColumns}
          rows={visibleRows}
          selectedRowIds={selectedRowIds}
          onSelectedRowIdsChange={setSelectedRowIds}
          totalRowCount={tableRows.length}
        />
      </AppTablePane>
    </div>
  );
}

export default function TablePatternsDocs() {
  return (
    <DocsSectionCard title="Tables">
      <div className="ds-stack">
        <div className="ds-docs-spotlight-grid ds-docs-spotlight-grid--tables">
          <DocsPreviewPanel title="AppTable">
            <div className="ds-docs-preview-surface ds-docs-table-preview-surface">
              <MainTablePreview />
            </div>
          </DocsPreviewPanel>

          <DocsNiceToKnow
            items={niceToKnowItems}
            highlightTitle="Pro Spalte definieren:"
            highlightItems={columnDefinitionHighlights}
          />
        </div>
      </div>
    </DocsSectionCard>
  );
}
